/*
 * Speedometer
 * @module components
 * @flow
 */

import React from 'react';
import styled, { css } from 'styled-components/macro';
import { ifProp } from 'styled-tools';
import * as R from 'ramda';

import { DataValue } from '~/components';

const RADIUS = 122;
const circleLength = radius => Math.floor(2 * Math.PI * radius);
const tickMark = R.pipe(
  circleLength,
  R.multiply(7.5e-3),
);

export type SpeedometerProps = {
  hint: string,
  value: number,
  valuePtc: number,
  valueMetrics: string,
  device: $PropertyType<DeviceQuery, 'device'>,
};

const SpeedometerWrapper = styled.div`
  position: relative;
  margin: auto;
  width: 300px;
  height: 300px;
`;

const SpeedometerSVG = styled.svg`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  transform: rotate(135deg);
`;

const SpeedometerPercentageValue = styled.span`
  position: absolute;
  top: 19px;
  left: 0;
  width: 100%;
  font-weight: 600;
  text-align: center;
`;

const SpeedometerValueWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  align-items: center;
  justify-content: center;
`;

const SpeedometerValue = styled.span`
  display: inline-block;
  margin-bottom: 12px;
  width: 200px;
  font-size: 36px;
  font-weight: 600;
  text-align: center;
`;

const SpeedometerValueMetrics = styled.span`
  color: #a2a2a2;
  font-size: 26px;
  font-weight: 600;
`;

const SpeedometerHint = styled.span`
  position: absolute;
  left: -70px;
  bottom: 45px;
  max-width: 100px;
  color: #bcbcbc;
  font-weight: 600;
  white-space: nowrap;

  ${ifProp(
    { device: 'mobile' },
    css`
      @media (max-width: 500px) {
        left: 0;
        right: 0;
        display: block;
        max-width: none;
      }
    `,
  )}
`;

/**
 * Speedometer React-component
 * @memberof components
 * @param {Object} props - properties
 * @param {string} [props.int] - hint
 * @param {number} [props.value] - value
 * @param {number} [props.valuePtc] - percentage value
 * @param {string} [props.valueMetrics] - value metrics
 * @param {$PropertyType<DeviceQuery, 'device'>} [props.device]s - device mode
 * @example <caption>Simple usage</caption>
 * <Speedometer />
 * @returns {React.Node<*>} React-component
 */
export const Speedometer = ({
  hint,
  value,
  valuePtc,
  valueMetrics,
  device,
}: SpeedometerProps) => (
  <SpeedometerWrapper>
    <SpeedometerValueWrapper>
      <SpeedometerValue>
        <DataValue value={value} />
      </SpeedometerValue>
      <SpeedometerValueMetrics>{valueMetrics}</SpeedometerValueMetrics>
    </SpeedometerValueWrapper>
    <SpeedometerSVG>
      <circle
        r={RADIUS}
        cx="50%"
        cy="50%"
        stroke="#9bd0ff"
        stroke-width="19"
        stroke-dasharray={`${valuePtc * tickMark(RADIUS)}, ${circleLength(
          RADIUS,
        )}`}
        fill="none"
      />
      <circle
        r="135"
        cx="50%"
        cy="50%"
        stroke="#6890cf"
        stroke-width="7"
        stroke-dasharray="636, 848"
        fill="none"
      />
      <circle
        r="110"
        cx="50%"
        cy="50%"
        stroke="#6890cf"
        stroke-width="6"
        stroke-dasharray="518, 691"
        fill="none"
      />
      <line
        x1="257"
        y1="149"
        x2="288.5"
        y2="149"
        stroke="#6890cf"
        stroke-width="4"
      />
      <line
        x1="151"
        y1="11.5"
        x2="151"
        y2="43"
        stroke="#6890cf"
        stroke-width="4"
      />
    </SpeedometerSVG>
    <SpeedometerPercentageValue>
      <DataValue
        // eslint-disable-next-line react/style-prop-object
        style="percent"
        value={valuePtc / 100}
        percents={true}
      />
    </SpeedometerPercentageValue>
    <SpeedometerHint device={device}>{hint}</SpeedometerHint>
  </SpeedometerWrapper>
);

Speedometer.defaultProps = {
  value: 0,
  valuePtc: 0,
  hint: '',
};
